import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard,
  ShieldCheck,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import {
  GatewayCredentialField,
  PaymentGatewayAdminResponse,
  PaymentGatewayConfigRequest,
} from '../Types/PaymentGatewayType';
import {
  savePaymentGateway,
  setPaymentGatewayEnabled,
} from '../Services/PaymentGatewayAdminService';

interface Props {
  data: PaymentGatewayAdminResponse[];
  onChanged: () => void;
}

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

/**
 * A secret input that never holds the real value.
 *
 * <p>Placeholder is derived from {@link PaymentGatewayAdminResponse.hasSecret}, never from the stored
 * value, because the stored value never reaches the browser. An admin opening this form sees
 * "stored" and leaves the box alone; an admin replacing a key types over it and submits, which sends
 * a real value and overwrites. Sending back a masked placeholder as if it were a key is the failure
 * mode this avoids.
 */
const SecretField = ({
  label,
  hasStoredValue,
  isSaving,
  onSave,
  helpText,
  required,
}: {
  label: string;
  hasStoredValue: boolean;
  isSaving: boolean;
  onSave: (value: string) => void;
  helpText?: string;
  required?: boolean;
}) => {
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState(false);

  // Discard any in-progress typing when the selection moves, so a key typed for one gateway is
  // never carried over to another by a stray save.
  useEffect(() => {
    setValue('');
    setRevealed(false);
  }, [label, hasStoredValue]);

  const trimmed = value.trim();
  const dirty = trimmed.length > 0;

  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </label>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type={revealed ? 'text' : 'password'}
            value={value}
            autoComplete="new-password"
            onChange={(e) => setValue(e.target.value)}
            placeholder={hasStoredValue ? 'Stored - leave blank to keep' : 'Not configured yet'}
            className={fieldClass}
          />
          <button
            type="button"
            onClick={() => setRevealed((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400 transition-colors"
            title={revealed ? 'Hide' : 'Show'}
          >
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <button
          type="button"
          disabled={!dirty || isSaving}
          onClick={() => onSave(trimmed)}
          className="shrink-0 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 text-[10px] font-black uppercase tracking-widest text-cyan-300 transition hover:bg-cyan-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Set
        </button>
      </div>

      {hasStoredValue && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400">
          <Lock className="w-3 h-3" />
          Stored on the server - encrypted, never sent to this page
        </p>
      )}

      {helpText && <p className="mt-1.5 text-[11px] text-slate-500">{helpText}</p>}
    </div>
  );
};

const StatusPill = ({ label, tone }: { label: string; tone: 'ok' | 'warn' | 'off' }) => {
  const tones = {
    ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
    warn: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    off: 'border-slate-700 bg-slate-950 text-slate-400',
  };
  return (
    <span className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${tones[tone]}`}>
      {label}
    </span>
  );
};

const PaymentGatewayConfigComponent: React.FC<Props> = ({ data, onChanged }) => {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  // Sorted for display; selection is tracked by code so it survives a refetch reordering the list.
  const ordered = useMemo(
    () => [...data].sort((a, b) => a.displayOrder - b.displayOrder),
    [data]
  );

  const selected = useMemo(
    () => ordered.find((g) => g.gateway === selectedCode) || ordered[0] || null,
    [ordered, selectedCode]
  );

  useEffect(() => {
    if (!selectedCode && ordered.length > 0) {
      setSelectedCode(ordered[0].gateway);
    }
  }, [ordered, selectedCode]);

  const notify = (next: AlertProps) => {
    setAlert({ ...next, onClose: () => setAlert(null) });
  };

  const fail = (error: unknown, fallback: string) => {
    const message = error instanceof Error ? error.message : fallback;
    notify({ variant: 'error', title: 'Gateway configuration', message });
  };

  const handleSaveMeta = async (gateway: PaymentGatewayAdminResponse, patch: PaymentGatewayConfigRequest) => {
    setSavingField('meta');
    try {
      const response = await savePaymentGateway(gateway.gateway, patch);
      if (response?.success) {
        onChanged();
        notify({
          variant: 'success',
          title: 'Saved',
          message: `${gateway.displayName} configuration updated.`,
        });
        return;
      }
      fail(null, response?.message || 'Unable to save the gateway configuration');
    } catch (error) {
      fail(error, 'Unable to save the gateway configuration');
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveSecret = async (
    gateway: PaymentGatewayAdminResponse,
    field: 'secretKey' | string,
    value: string
  ) => {
    setSavingField(field);
    try {
      const extraFields = field.startsWith('extra:')
        ? { [field.slice('extra:'.length)]: value }
        : undefined;

      const payload: PaymentGatewayConfigRequest = extraFields
        ? { extraFields }
        : field === 'secretKey'
        ? { secretKey: value }
        : { publishableKey: value };

      const response = await savePaymentGateway(gateway.gateway, payload);
      if (response?.success) {
        onChanged();
        notify({
          variant: 'success',
          title: 'Saved',
          message: `${gateway.displayName} credential updated.`,
        });
        return;
      }
      fail(null, response?.message || 'Unable to save the credential');
    } catch (error) {
      fail(error, 'Unable to save the credential');
    } finally {
      setSavingField(null);
    }
  };

  const handleToggle = async (gateway: PaymentGatewayAdminResponse, isEnabled: boolean) => {
    setSavingField('toggle');
    try {
      const response = await setPaymentGatewayEnabled(gateway.gateway, isEnabled);
      if (response?.success) {
        onChanged();
        notify({
          variant: isEnabled ? 'success' : 'info',
          title: isEnabled ? 'Gateway enabled' : 'Gateway disabled',
          message: isEnabled
            ? `${gateway.displayName} is now offered at checkout.`
            : `${gateway.displayName} is hidden from checkout. Existing payments still verify.`,
        });
        return;
      }
      fail(null, response?.message || 'Unable to change the gateway state');
    } catch (error) {
      fail(error, 'Unable to change the gateway state');
    } finally {
      setSavingField(null);
    }
  };

  if (ordered.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center">
        <p className="text-sm text-slate-400">No payment gateways are available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}

      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          Secrets entered here are encrypted on the server and are never sent back to this page. Saving a
          new value overwrites the stored one; leaving the box blank keeps what is already stored. Nothing
          takes effect at checkout until the gateway is complete and enabled.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Gateway list */}
        <div className="space-y-3">
          {ordered.map((gateway) => {
            const isActive = gateway.gateway === selected?.gateway;
            const busy = savingField !== null && isActive;

            return (
              <button
                key={gateway.gateway}
                type="button"
                onClick={() => setSelectedCode(gateway.gateway)}
                className={`w-full text-left rounded-2xl border p-4 transition ${
                  isActive
                    ? 'border-cyan-500/50 bg-slate-900'
                    : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-cyan-400" />
                    <span className="font-bold text-sm text-slate-100">{gateway.displayName}</span>
                  </div>
                  {busy && <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />}
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {gateway.isSelectable ? (
                    <StatusPill label="Live" tone="ok" />
                  ) : gateway.isConfigured ? (
                    <StatusPill label="Configured" tone="warn" />
                  ) : (
                    <StatusPill label="Incomplete" tone="off" />
                  )}
                  {gateway.isTestMode && <StatusPill label="Test mode" tone="warn" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <motion.div
            key={selected.gateway}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 space-y-6"
          >
            {/* Header + enable toggle */}
            <div className="flex flex-wrap items-start justify-between gap-4 pb-5 border-b border-slate-800">
              <div>
                <h3 className="text-lg font-black text-white">{selected.displayName}</h3>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{selected.gateway}</p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  {selected.isEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={selected.isEnabled}
                  disabled={savingField === 'toggle'}
                  onClick={() => handleToggle(selected, !selected.isEnabled)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    selected.isEnabled ? 'bg-emerald-500' : 'bg-slate-700'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      selected.isEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </label>
            </div>

            {!selected.isConfigured && (
              <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/80 leading-relaxed">
                  {selected.displayName} is missing required credentials and cannot be enabled. Fill in
                  every required field below, then save.
                </p>
              </div>
            )}

            {/* Presentation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Display name</label>
                <input
                  defaultValue={selected.displayName}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && value !== selected.displayName) {
                      handleSaveMeta(selected, { displayName: value });
                    }
                  }}
                  className={fieldClass}
                />
              </div>

              <div>
                <label className={labelClass}>Checkout order</label>
                <input
                  type="number"
                  defaultValue={selected.displayOrder}
                  onBlur={(e) => {
                    const value = Number(e.target.value);
                    if (Number.isFinite(value) && value !== selected.displayOrder) {
                      handleSaveMeta(selected, { displayOrder: value });
                    }
                  }}
                  className={fieldClass}
                />
                <p className="mt-1.5 text-[11px] text-slate-500">
                  Lower appears first in the customer's payment picker.
                </p>
              </div>
            </div>

            {/* Credentials */}
            <div className="space-y-5 pt-5 border-t border-slate-800">
              <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Credentials
              </h4>

              <SecretField
                label="Publishable key"
                hasStoredValue={Boolean(selected.publishableKey)}
                isSaving={savingField === 'publishableKey'}
                required
                onSave={(value) => handleSaveSecret(selected, 'publishableKey', value)}
                helpText="Public by design - this is what the browser receives to open the checkout."
              />

              <SecretField
                label="Secret key"
                hasStoredValue={selected.hasSecret}
                isSaving={savingField === 'secretKey'}
                required
                onSave={(value) => handleSaveSecret(selected, 'secretKey', value)}
                helpText="Used only for signature verification and order creation on the server."
              />

              {selected.extraFields.map((field: GatewayCredentialField) => (
                <SecretField
                  key={field.key}
                  label={field.label}
                  hasStoredValue={selected.extraFieldKeysPresent.includes(field.key)}
                  isSaving={savingField === `extra:${field.key}`}
                  required={field.required}
                  onSave={(value) => handleSaveSecret(selected, `extra:${field.key}`, value)}
                  helpText={field.helpText}
                />
              ))}

              {!selected.extraFields.length && (
                <p className="text-xs text-slate-500">This provider needs no additional fields.</p>
              )}
            </div>

            {/* Remarks */}
            <div className="pt-5 border-t border-slate-800">
              <label className={labelClass}>Internal remarks</label>
              <textarea
                defaultValue={selected.remarks ?? ''}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value !== (selected.remarks ?? '')) {
                    handleSaveMeta(selected, { remarks: value || undefined });
                  }
                }}
                rows={2}
                className={fieldClass}
              />
              <p className="mt-1.5 text-[11px] text-slate-500">
                Never shown to customers. Handy for which account or environment these keys belong to.
              </p>
            </div>

            {/* Summary */}
            <div className="flex flex-wrap items-center gap-3 pt-5 border-t border-slate-800">
              {selected.isSelectable ? (
                <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  Offered at checkout
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                  <RefreshCw className="w-4 h-4" />
                  Hidden from checkout
                </span>
              )}
              {selected.updatedAt && (
                <span className="text-[11px] text-slate-600 ml-auto">
                  Last updated {new Date(selected.updatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onChanged}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-300"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>
    </div>
  );
};

export default PaymentGatewayConfigComponent;